import { useEffect, useState } from "react";
import { toast } from "sonner";
import { formatDistanceToNow } from "date-fns";
import { useSearchParams } from "react-router-dom";
import api, { apiErr } from "../lib/api";
import { getCache, setCache, hasCache } from "../lib/swr";
import { useAuth } from "../context/AuthContext";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Textarea } from "../components/ui/textarea";
import { Label } from "../components/ui/label";
import { Switch } from "../components/ui/switch";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "../components/ui/tabs";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "../components/ui/dialog";
import { Plus, HandHeart, AlertTriangle, Loader2, CheckCircle2, Lock, Globe, MessageCircle, Send, X, Pencil, Trash2, Inbox } from "lucide-react";
import { tabsListClass, tabTriggerClass } from "../lib/tabStyles";
import { EmptyState } from "../components/EmptyState";
import { SectionBanner } from "../components/SectionBanner";
import CommentThread from "../components/CommentThread";

export default function Prayer() {
  const { user, can } = useAuth();
  const isAdmin = user?.role === "admin";
  const canPrayer = can ? can("prayer") : isAdmin;
  const [params] = useSearchParams();
  const [tab, setTab] = useState(params.get("tab") === "inbox" ? "inbox" : "forus");
  const [items, setItems] = useState(() => getCache("prayer:items") || []);
  const [loading, setLoading] = useState(() => !hasCache("prayer:items"));
  const [dialog, setDialog] = useState(null); // {mode:'create'|'edit', prayer, fromAdmin}
  const [saving, setSaving] = useState(false);
  const [form, setForm] = useState({ title: "", body: "", is_urgent: false, is_public: true });

  const load = async () => {
    try { const { data } = await api.get("/prayers"); setItems(data); setCache("prayer:items", data); }
    catch (e) { toast.error(apiErr(e)); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  const openCreate = () => { setForm({ title: "", body: "", is_urgent: false, is_public: true }); setDialog({ mode: "create" }); };
  const openEdit = (p) => { setForm({ title: p.title || "", body: p.body, is_urgent: p.is_urgent, is_public: p.is_public }); setDialog({ mode: "edit", prayer: p }); };

  const submit = async () => {
    if (!form.body.trim()) return toast.error("Please write your request");
    setSaving(true);
    try {
      if (dialog.mode === "edit") {
        const { data } = await api.put(`/prayers/${dialog.prayer.id}`, form);
        setItems((prev) => prev.map((x) => (x.id === data.id ? { ...x, ...data } : x)));
        toast.success("Updated");
      } else {
        const { data } = await api.post("/prayers", form);
        setItems([data, ...items]);
        toast.success("Prayer request shared");
      }
      setDialog(null);
    } catch (e) { toast.error(apiErr(e)); }
    finally { setSaving(false); }
  };

  const pray = async (id) => {
    try {
      const { data } = await api.post(`/prayers/${id}/pray`);
      setItems((prev) => prev.map((p) => (p.id === id ? { ...p, pray_count: data.pray_count, prayed: data.prayed } : p)));
    } catch (e) { toast.error(apiErr(e)); }
  };
  const respond = async (id, response, status) => {
    try { await api.post(`/prayers/${id}/respond`, { response, status });
      setItems((prev) => prev.map((p) => (p.id === id ? { ...p, response, status } : p))); toast.success("Response saved");
    } catch (e) { toast.error(apiErr(e)); }
  };
  const remove = async (id) => {
    try { await api.delete(`/prayers/${id}`); setItems((prev) => prev.filter((p) => p.id !== id)); toast.success("Removed"); }
    catch (e) { toast.error(apiErr(e)); }
  };

  const forUs = items.filter((p) => p.from_admin);
  const wall = items.filter((p) => !p.from_admin && (p.is_public || p.author_id === user?.id));
  const inbox = items.filter((p) => !p.from_admin && !p.is_public);

  const listView = (arr, emptyCfg) => (
    loading ? <div className="flex justify-center py-16"><Loader2 className="h-6 w-6 animate-spin accent-text" /></div>
      : arr.length === 0 ? (
          <EmptyState
            testid={emptyCfg.testid}
            icon={emptyCfg.icon || HandHeart}
            title={emptyCfg.title}
            description={emptyCfg.description}
            actionLabel={emptyCfg.actionLabel}
            onAction={emptyCfg.onAction}
          />
        )
        : <div className="flex flex-col gap-4">{arr.map((p) => (
            <PrayerItem key={p.id} p={p} user={user} isAdmin={isAdmin} canRespond={canPrayer} onPray={pray} onRespond={respond} onEdit={openEdit} onDelete={remove} />
          ))}</div>
  );

  return (
    <div>
      <SectionBanner title="Prayer" subtitle="Lift the mission in prayer" icon={HandHeart} accent="#E0A73A" />

      <Tabs value={tab} onValueChange={setTab}>
        <TabsList className={tabsListClass(isAdmin ? 3 : 2)}>
          <TabsTrigger value="forus" className={tabTriggerClass} data-testid="tab-forus"><HandHeart className="h-4 w-4" /> Pray for Us</TabsTrigger>
          <TabsTrigger value="wall" className={tabTriggerClass} data-testid="tab-wall"><MessageCircle className="h-4 w-4" /> Prayer Wall</TabsTrigger>
          {isAdmin && (
            <TabsTrigger value="inbox" className={`${tabTriggerClass} relative`} data-testid="tab-inbox">
              <Inbox className="h-4 w-4" /> Inbox{inbox.length > 0 && <span className="ml-0.5 min-w-[18px] h-[18px] px-1 rounded-full bg-destructive text-white text-[10px] font-bold inline-flex items-center justify-center">{inbox.length}</span>}
            </TabsTrigger>
          )}
        </TabsList>

        <TabsContent value="forus">
          <p className="text-sm text-muted-foreground mb-4">Prayer needs from the team as we travel and serve.</p>
          {isAdmin && (
            <Button data-testid="new-forus-btn" onClick={openCreate} className="rounded-full accent-bg text-white hover:opacity-90 mb-4"><Plus className="h-4 w-4 mr-1" /> New request</Button>
          )}
          {listView(forUs, {
            testid: "forus-empty", icon: HandHeart,
            title: "No prayer needs shared yet",
            description: canPrayer ? "Post what the team needs prayer for as you travel and serve." : "Prayer needs from the team will appear here as they travel and serve.",
            actionLabel: canPrayer ? "Post a request" : undefined,
            onAction: canPrayer ? openCreate : undefined,
          })}
        </TabsContent>

        <TabsContent value="wall">
          <p className="text-sm text-muted-foreground mb-4">Share your own requests and pray for one another.</p>
          {!isAdmin && (
            <Button data-testid="new-prayer-btn" onClick={openCreate} className="rounded-full accent-bg text-white hover:opacity-90 mb-4"><Plus className="h-4 w-4 mr-1" /> Request prayer</Button>
          )}
          {listView(wall, {
            testid: "wall-empty", icon: HandHeart,
            title: "The prayer wall is quiet",
            description: isAdmin ? "When partners share requests, they'll show up here for everyone to pray over." : "Be the first to share a request — your partners are ready to pray with you.",
            actionLabel: !isAdmin ? "Request prayer" : undefined,
            onAction: !isAdmin ? openCreate : undefined,
          })}
        </TabsContent>

        {isAdmin && (
          <TabsContent value="inbox">
            <p className="text-sm text-muted-foreground mb-4">Private requests sent directly to the leaders. Only you can see these.</p>
            {listView(inbox, {
              testid: "inbox-empty", icon: Inbox,
              title: "No private requests",
              description: "Requests partners send privately to the leaders will appear here. Only you can see them.",
            })}
          </TabsContent>
        )}
      </Tabs>

      <Dialog open={!!dialog} onOpenChange={(v) => !v && setDialog(null)}>
        <DialogContent className="rounded-3xl max-w-md">
          <DialogHeader><DialogTitle className="font-heading text-[var(--brand-navy)]">{dialog?.mode === "edit" ? "Edit request" : "Share a prayer request"}</DialogTitle><DialogDescription className="sr-only">Prayer request form</DialogDescription></DialogHeader>
          <div className="space-y-4 mt-2">
            <Input data-testid="prayer-title-input" placeholder="Title (optional)" className="rounded-xl h-11" value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} />
            <Textarea data-testid="prayer-body-input" placeholder="What can we pray for?" className="rounded-xl min-h-28" value={form.body} onChange={(e) => setForm({ ...form, body: e.target.value })} />
            <div className="flex items-center justify-between">
              <Label className="text-sm flex items-center gap-2"><AlertTriangle className="h-4 w-4 text-destructive" /> Mark as urgent</Label>
              <Switch data-testid="prayer-urgent-switch" checked={form.is_urgent} onCheckedChange={(v) => setForm({ ...form, is_urgent: v })} />
            </div>
            <div className="flex items-center justify-between">
              <Label className="text-sm flex items-center gap-2">{form.is_public ? <Globe className="h-4 w-4 text-[var(--brand-navy)]" /> : <Lock className="h-4 w-4" />}{form.is_public ? "Visible to all partners" : "Private to leaders"}</Label>
              <Switch data-testid="prayer-public-switch" checked={form.is_public} onCheckedChange={(v) => setForm({ ...form, is_public: v })} />
            </div>
            <Button data-testid="prayer-submit-btn" disabled={saving} onClick={submit} className="w-full rounded-full accent-bg text-white h-11 hover:opacity-90">{saving ? "Saving…" : dialog?.mode === "edit" ? "Save changes" : "Share request"}</Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}

function PrayerItem({ p, user, isAdmin, canRespond, onPray, onRespond, onEdit, onDelete }) {
  const [resp, setResp] = useState(p.response || "");
  const [open, setOpen] = useState(true);
  const [comments, setComments] = useState([]);
  const [count, setCount] = useState(p.comment_count || 0);
  const when = p.created_at ? formatDistanceToNow(new Date(p.created_at), { addSuffix: true }) : "";
  const mine = p.author_id === user?.id;

  // Comments open by default under each prayer request to invite encouragement.
  useEffect(() => {
    let active = true;
    (async () => {
      try { const { data } = await api.get(`/prayers/${p.id}/comments`); if (active) setComments(data); }
      catch (e) { /* non-blocking */ }
    })();
    return () => { active = false; };
  }, [p.id]);

  const toggle = async () => {
    if (!open && comments.length === 0) { try { const { data } = await api.get(`/prayers/${p.id}/comments`); setComments(data); } catch (e) { toast.error(apiErr(e)); } }
    setOpen(!open);
  };

  return (
    <div className="bg-white rounded-2xl p-5 shadow-[0_4px_24px_rgba(44,44,62,0.04)] animate-fade-up" data-testid="prayer-item">
      <div className="flex items-start justify-between gap-2">
        <div className="flex items-center gap-2 mb-1 flex-wrap">
          {p.is_urgent && <span className="text-xs font-semibold text-destructive bg-destructive/10 px-2 py-0.5 rounded-full flex items-center gap-1"><AlertTriangle className="h-3 w-3" /> Urgent</span>}
          {!p.is_public && <span className="text-xs text-muted-foreground flex items-center gap-1"><Lock className="h-3 w-3" /> Private</span>}
          {p.status === "answered" && <span className="text-xs font-semibold text-[var(--brand-navy)] flex items-center gap-1"><CheckCircle2 className="h-3 w-3" /> Answered</span>}
        </div>
        {(isAdmin || mine) && (
          <div className="flex items-center gap-2 shrink-0">
            <button data-testid="prayer-edit-btn" onClick={() => onEdit(p)} className="text-muted-foreground hover:text-[var(--brand-navy)]"><Pencil className="h-4 w-4" /></button>
            <button data-testid="prayer-delete-btn" onClick={() => onDelete(p.id)} className="text-muted-foreground hover:text-destructive"><Trash2 className="h-4 w-4" /></button>
          </div>
        )}
      </div>
      {p.title && <h3 className="font-heading font-bold text-lg text-[#2C2C3E]">{p.title}</h3>}
      <p className="text-[15px] text-[#2C2C3E]/90 mt-1 whitespace-pre-wrap">{p.body}</p>
      <div className="text-xs text-muted-foreground mt-1">{p.author_name} · {when}</div>

      {p.response && (
        <div className="mt-3 rounded-xl bg-[var(--brand-navy)]/5 p-3 text-sm"><div className="text-xs font-semibold text-[var(--brand-navy)] mb-0.5">Leader's response</div>{p.response}</div>
      )}

      <div className="flex items-center gap-2 mt-4">
        <button data-testid="pray-btn" onClick={() => onPray(p.id)}
          className={`flex items-center gap-1.5 rounded-full px-3 py-1.5 text-sm border transition-transform hover:scale-105 active:scale-95 ${p.prayed ? "accent-border bg-[var(--tenant-accent)]/10 accent-text" : "border-border"}`}>
          <HandHeart className="h-4 w-4" /> Praying {p.pray_count > 0 && `· ${p.pray_count}`}
        </button>
        <button data-testid="prayer-comment-toggle" onClick={toggle} className="ml-auto flex items-center gap-1.5 text-sm text-muted-foreground hover:text-[var(--brand-navy)]"><MessageCircle className="h-4 w-4" /> {count}</button>
      </div>

      {open && (
        <div className="mt-4 border-t pt-4">
          <CommentThread basePath={`/prayers/${p.id}`} userId={user?.id} isAdmin={isAdmin}
            comments={comments} setComments={setComments} onCountChange={setCount}
            placeholder="Write an encouragement…" />
        </div>
      )}

      {canRespond && (
        <div className="mt-3 flex gap-2">
          <Input data-testid="prayer-response-input" value={resp} onChange={(e) => setResp(e.target.value)} placeholder="Reply privately…" className="rounded-full h-9 text-sm" />
          <Button data-testid="prayer-respond-btn" size="sm" variant="outline" className="rounded-full" onClick={() => onRespond(p.id, resp, "acknowledged")}>Reply</Button>
          <Button data-testid="prayer-answered-btn" size="sm" className="rounded-full accent-bg text-white" onClick={() => onRespond(p.id, resp, "answered")}>Answered</Button>
        </div>
      )}
    </div>
  );
}
